import { IconButton } from '@mui/material'
import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import EjercicioSelector from '../../../../components/form/EjercicioSelector/EjercicioSelector'
import { CENTROS } from '../../../../utils/academia'
import { format, formatForExport } from '../../../../utils/math'
import { downloadCSVFile, getCSVContent } from '../../../../utils/csv'
import MesSelector from '../../../../components/form/MesSelector/MesSelector'
import PageLoading from '../../../../components/PageLoading/PageLoading'
import InformationTable from '../../../../components/Table/InformationTable'
import TableHeader from '../../../../components/TableHeader/TableHeader'
import { useNotification } from '../../../../context/NotificationManager/NotificationManager'
import { getLeadListCentral } from '../../../../utils/api/leads'
import css from './AnalisisCentral.module.css'

const STARTING_VALUE = {
  leads: 0,
  pruebas: 0,
  captados: 0,
  abiertos: 0,
  novalidos: 0,
  derivados: 0
}

const processDatos = ({ leads }) => {
  const centros = []
  // First: central
  centros.push({ name: `Central`, value: { ...STARTING_VALUE } })
  centros.push({ name: `Central (Web)`, value: { ...STARTING_VALUE } })
  centros.push({ name: `Central (SEM)`, value: { ...STARTING_VALUE } })
  // Fill with initial values
  CENTROS.forEach(m => {
    centros.push({ name: `${m}`, value: { ...STARTING_VALUE } })
    centros.push({ name: `${m} (Web)`, value: { ...STARTING_VALUE } })
    centros.push({ name: `${m} (SEM)`, value: { ...STARTING_VALUE } })
  })
  // Process the leads
  leads.forEach(lead => {
    const centro = centros.find(m => m.name === (lead.centro || 'Central'))
    const centro_web = centros.find(
      m => m.name === `${lead.centro || 'Central'} (Web)`
    )
    const centro_sem = centros.find(
      m => m.name === `${lead.centro || 'Central'} (SEM)`
    )

    centro.value.leads++
    if (lead.prueba) centro.value.pruebas++
    if (
      !lead.estado.startsWith('CLIENTE') &&
      lead.estado !== 'LEAD_NO_VALIDO' &&
      lead.estado !== 'LEAD_DERIVADO'
    )
      centro.value.abiertos++
    if (lead.estado === 'CLIENTE_CAPTADO') centro.value.captados++
    if (lead.estado === 'LEAD_NO_VALIDO') centro.value.novalidos++
    if (lead.estado === 'LEAD_DERIVADO') centro.value.derivados++

    if (lead.origenWeb) {
      const isSem = lead.origenSEM
      centro_web.value.leads++
      if (isSem) centro_sem.value.leads++
      if (lead.prueba) centro_web.value.pruebas++
      if (isSem && lead.prueba) centro_sem.value.pruebas++
      if (
        !lead.estado.startsWith('CLIENTE') &&
        lead.estado !== 'LEAD_NO_VALIDO' &&
        lead.estado !== 'LEAD_DERIVADO'
      )
        centro_web.value.abiertos++
      if (
        isSem &&
        !lead.estado.startsWith('CLIENTE') &&
        lead.estado !== 'LEAD_NO_VALIDO' &&
        lead.estado !== 'LEAD_DERIVADO'
      )
        centro_sem.value.abiertos++
      if (lead.estado === 'CLIENTE_CAPTADO') centro_web.value.captados++
      if (isSem && lead.estado === 'CLIENTE_CAPTADO')
        centro_sem.value.captados++
      if (lead.estado === 'LEAD_NO_VALIDO') centro_web.value.novalidos++
      if (isSem && lead.estado === 'LEAD_NO_VALIDO')
        centro_sem.value.novalidos++
      if (lead.estado === 'LEAD_DERIVADO') centro_web.value.derivados++
      if (isSem && lead.estado === 'LEAD_DERIVADO') centro_sem.value.derivados++
    }
  })

  // Generate totals
  const totals_sem = { ...STARTING_VALUE }
  const totals_web = { ...STARTING_VALUE }
  const total_leads = { ...STARTING_VALUE }

  const combine = (totals, mes) => {
    totals.leads += mes.leads
    totals.pruebas += mes.pruebas
    totals.captados += mes.captados
    totals.abiertos += mes.abiertos
    totals.novalidos += mes.novalidos
    totals.derivados += mes.derivados
  }

  centros.forEach(mes => {
    if (mes.name.endsWith('(Web)')) combine(totals_web, mes.value)
    else if (mes.name.endsWith('(SEM)')) combine(totals_sem, mes.value)
    else combine(total_leads, mes.value)
  })

  return [
    ...centros.filter(c => {
      // Siempre dejamos cental
      if (c.name.startsWith('Central')) return true
      // Borramos los datos de (Web) o (SEM) si el centro principal no tiene leads
      if (c.name.endsWith('(Web)')) {
        const main = centros.find(m => m.name === c.name.replace(' (Web)', ''))
        return c.value.leads > 0 || main?.value.leads > 0
      }
      if (c.name.endsWith('(SEM)')) {
        const main = centros.find(m => m.name === c.name.replace(' (SEM)', ''))
        return c.value.leads > 0 || main?.value.leads > 0
      }
      // Para los centros principales, solo mostramos si tienen leads
      return c.value.leads > 0
    }),
    { name: 'Total', value: total_leads },
    { name: 'Total (Web)', value: totals_web },
    { name: 'Total (SEM)', value: totals_sem }
  ]
}

const AnalisisCentral = () => {
  const notification = useNotification()

  const [mes, setMes] = useState(new Date().getMonth() + 1)
  const [ejercicio, setEjercicio] = useState(new Date().getFullYear())

  const { isLoading: isLoadingLeads, data: feedbackList = [] } = useQuery({
    queryKey: ['feedback', 'list', 'central', mes, ejercicio],
    queryFn: () => {
      const fecha_inicio = `${mes}/${ejercicio}`
      const fecha_fin = `${(mes % 12) + 1}/${mes < 12 ? ejercicio : ejercicio + 1}`
      return getLeadListCentral({ fecha_inicio, fecha_fin })
        .then(datos => datos)
        .catch(err => {
          notification.error({
            title: 'Error al recuperar los leads',
            content: err
          })
          return []
        })
    }
  })

  const handleExport = () => {
    const content = getCSVContent({
      columns: [
        'Centro',
        'Leads',
        'No válidos',
        'Derivados',
        'Pruebas',
        'Abiertos',
        'Captados',
        'Lead → Prueba',
        'Prueba → Alumno',
        'Lead → Alumno'
      ],
      data: processDatos({ leads: feedbackList }),
      transform: c => [
        c.name,
        `${c.value.leads}`,
        `${c.value.novalidos}`,
        `${c.value.derivados}`,
        `${c.value.pruebas}`,
        `${c.value.captados}`,
        `${c.value.abiertos}`,
        `${formatForExport((100 * c.value.pruebas) / (c.value.leads - c.value.novalidos - c.value.derivados))}%`,
        `${formatForExport((100 * c.value.captados) / c.value.pruebas)}%`,
        `${formatForExport((100 * c.value.captados) / (c.value.leads - c.value.novalidos - c.value.derivados))}%`
      ]
    })
    downloadCSVFile(content, 'captacion-mes')
  }

  return (
    <PageLoading isLoading={isLoadingLeads}>
      <TableHeader
        actions={
          <div className={css.actions}>
            <MesSelector
              name='mes'
              label='Mes'
              value={mes}
              onChange={e => setMes(e.target.value)}
            />
            <EjercicioSelector
              className={css.year}
              name='ejercicio'
              label='Ejercicio'
              value={ejercicio}
              onChange={e => setEjercicio(e.target.value)}
            />
            <IconButton
              className={css.download}
              size='small'
              color='primary'
              onClick={handleExport}
            >
              <i className='material-icons'>download</i>
            </IconButton>
          </div>
        }
        title='Distrubución leads Central'
        showSearch={false}
      />
      <InformationTable
        details={[
          { title: 'Centro', key: 'centro', align: 'left' },
          { title: 'Leads', key: 'leads', sortDisabled: true, align: 'center' },
          {
            title: 'No válidos',
            key: 'no_validos',
            sortDisabled: true,
            align: 'center'
          },
          {
            title: 'Derivados',
            key: 'derivados',
            sortDisabled: true,
            align: 'center'
          },
          {
            title: 'Pruebas',
            key: 'pruebas',
            sortDisabled: true,
            align: 'center'
          },
          {
            title: 'Abiertos',
            key: 'abiertos',
            sortDisabled: true,
            align: 'center'
          },
          {
            title: 'Captados',
            key: 'captados',
            sortDisabled: true,
            align: 'center'
          },
          {
            title: 'Lead → Prueba',
            key: 'conversion_lead_prueba',
            sortDisabled: true,
            align: 'center'
          },
          {
            title: 'Prueba → Alumno',
            key: 'conversion_prueba_alumno',
            sortDisabled: true,
            align: 'center'
          },
          {
            title: 'Lead → Alumno',
            key: 'conversion_lead_alumno',
            sortDisabled: true,
            align: 'center'
          }
        ]}
        data={processDatos({ leads: feedbackList }).map(centro => ({
          centro: centro.name.startsWith('Total') ? (
            <strong>{centro.name}</strong>
          ) : (
            centro.name
          ),
          leads: centro.value.leads,
          no_validos: (
            <span className={css.error}>{centro.value.novalidos}</span>
          ),
          derivados: (
            <span className={css.warning}>{centro.value.derivados}</span>
          ),
          pruebas: centro.value.pruebas,
          captados: (
            <span className={css.success}>{centro.value.captados}</span>
          ),
          abiertos: centro.value.abiertos,
          conversion_lead_prueba: `${format((100 * centro.value.pruebas) / (centro.value.leads - centro.value.novalidos - centro.value.derivados))}%`,
          conversion_prueba_alumno: `${format((100 * centro.value.captados) / centro.value.pruebas)}%`,
          conversion_lead_alumno: `${format((100 * centro.value.captados) / (centro.value.leads - centro.value.novalidos - centro.value.derivados))}%`
        }))}
      />
    </PageLoading>
  )
}

export default AnalisisCentral
